import {Component, Input, OnChanges, OnInit, SimpleChanges, ChangeDetectorRef, OnDestroy} from '@angular/core';
import {InsightsService} from "../../services/insights/insights.service";
import { PermissionsService } from '../../services/permissions/permissions.service';
import { OrganisationService } from '../../services/organisation/organisation.service';
import { of, Subscription } from 'rxjs';
import { switchMap } from 'rxjs/operators';

@Component({
  selector: 'app-pulse-performance-tabs',
  templateUrl: './pulse-performance-tabs.component.pug',
  styleUrls: ['./pulse-performance-tabs.component.scss'],
  standalone: false
})
export class PulsePerformanceTabsComponent implements OnInit, OnChanges, OnDestroy {

  @Input() start!: any;
  @Input() end!: any;
  @Input() request!: any;
  @Input() questionnaire!: any;
  @Input() department!: any;
  @Input() filterBy!: any;
  @Input() perfMetrics!: any[];

  activeTab: 'metrics' | 'departments' | 'comments' = 'metrics';
  questionComments: any[] = [];
  selectedQuestionId: number | null = null;
  selectedQuestionComments: any[] = [];
  selectedQuestionText: string = '';
  commentsCount = 0;
  orgBrandName: string = '';
  dropdownLabel: string = 'Select a question';
  dropdownOptions: any[] = [];
  paginationCriteria = {
    total: 0,
    rowsPerView: 20,
    currentPage: 1,
  };
  loadingQuestionComments = false;
  ENT_10363_PULSE_RELAUNCH = false;
  searchTitle = $localize`Sorry, more data is required to generate an AI summary.`;
  searchDescription = $localize`More data is required to generate a meaningful summary. Try widening your search.`;
  searchImg = 'https://assets.xref.com/omni/shared/seal_warning.svg';

  private organisationSubscription?: Subscription;
  private questionCommentsSubscription?: Subscription;
  private commentsSubscription?: Subscription;

  constructor(private insightsService: InsightsService,
              private permissionsService: PermissionsService,
              private organisationService: OrganisationService,
              private cdr: ChangeDetectorRef
             ) {
    this.ENT_10363_PULSE_RELAUNCH = this.permissionsService.isFeatureActivated('ENT-10363-pulse-relaunch');
  }

  ngOnInit(): void {
    if (this.ENT_10363_PULSE_RELAUNCH) {
      // Get organisation brand name for replacing {{organisation}} placeholder
      this.organisationSubscription = this.organisationService.getOrganisation().subscribe(
        (res) => {
          this.orgBrandName = res?.brand_name || '';
          // Update dropdown options if questions are already loaded
          if (this.questionComments.length > 0) {
            this.updateDropdownOptions();
          }
          // Update dropdown label if a question is already selected
          if (this.selectedQuestionId) {
            const question = this.questionComments.find(q => q.question_id === this.selectedQuestionId);
            if (question) {
              this.dropdownLabel = this.getQuestionTextWithOrgReplaced(question.question_text);
              this.selectedQuestionText = question.question_text;
            }
          }
        },
        (err: any) => {
          console.log(err);
        }
      );
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.ENT_10363_PULSE_RELAUNCH && (changes['start'] || changes['end'] || changes['request'] || changes['questionnaire'] || changes['department'] || changes['filterBy'])) {
      this.loadQuestionComments();
    }
  }

  setActiveTab(tab: 'metrics' | 'departments' | 'comments'): void {
    this.activeTab = tab;
    // Load question comments when switching to comments tab if not already loaded
    if (tab === 'comments' && this.questionComments.length === 0) {
      this.loadQuestionComments();
    }
  }

  private loadQuestionComments(): void {
    // Unsubscribe previous subscription if it exists
    if (this.questionCommentsSubscription) {
      this.questionCommentsSubscription.unsubscribe();
    }

    // Load questions list first, then chain to load comments for selected question
    this.questionCommentsSubscription = this.insightsService.getPulseQuestions(
      this.start, this.end, this.request, this.department, this.questionnaire
    ).pipe(
      switchMap(questionsData => {
        this.questionComments = questionsData?.questions || [];
        this.commentsCount = this.questionComments.reduce((sum, q) => sum + (q.comment_count || 0), 0);
        this.updateDropdownOptions();

        if (this.questionComments.length > 0) {
          // Auto-select first question
          const question = this.questionComments[0];
          this.selectedQuestionId = question.question_id;
          this.paginationCriteria.currentPage = 1;
          this.dropdownLabel = this.getQuestionTextWithOrgReplaced(question.question_text);
          this.selectedQuestionText = question.question_text;
          return this.insightsService.getPulseQuestionComments(
            this.start, this.end, this.request, this.department, this.questionnaire,
            question.question_id, this.paginationCriteria.rowsPerView, this.paginationCriteria.currentPage
          );
        }
        this.selectedQuestionId = null;
        this.selectedQuestionComments = [];
        this.selectedQuestionText = '';
        this.dropdownLabel = 'Select a question';
        this.paginationCriteria.total = 0;
        this.paginationCriteria.currentPage = 1;
        return of(null);
      })
    ).subscribe(data => {
      if (data) {
        this.selectedQuestionComments = data?.comments || [];
        this.paginationCriteria.total = data?.total || 0;
        if (data?.question_text) {
          this.selectedQuestionText = data.question_text;
        }
      }
    }, error => {
      console.log(error);
    });
  }

  private loadCommentsForQuestion(questionId: number): void {
    if (!questionId) return;

    // Unsubscribe previous subscription if it exists
    if (this.commentsSubscription) {
      this.commentsSubscription.unsubscribe();
    }

    this.loadingQuestionComments = true;

    this.commentsSubscription = this.insightsService.getPulseQuestionComments(
      this.start, this.end, this.request, this.department, this.questionnaire,
      questionId, this.paginationCriteria.rowsPerView, this.paginationCriteria.currentPage
    ).subscribe(data => {
      this.selectedQuestionComments = data?.comments || [];
      this.paginationCriteria.total = data?.total || 0;
      // Update question text if available
      if (data?.question_text) {
        this.selectedQuestionText = data.question_text;
      }
      this.loadingQuestionComments = false;
    }, error => {
      console.log(error);
      this.loadingQuestionComments = false;
    });
  }

  selectQuestion(questionId: number | string): void {
    const id = typeof questionId === 'string' ? parseInt(questionId, 10) : questionId;
    this.selectedQuestionId = id;
    // Reset pagination to first page when selecting a new question
    this.paginationCriteria.currentPage = 1;
    // Load paginated comments for the selected question
    this.loadCommentsForQuestion(id);
    // Update dropdown label
    const question = this.questionComments.find(q => q.question_id === id);
    if (question) {
      this.dropdownLabel = this.getQuestionTextWithOrgReplaced(question.question_text);
      this.selectedQuestionText = question.question_text;
    }
  }

  onPageChange(pageNumber: number): void {
    this.paginationCriteria.currentPage = pageNumber;
    if (this.selectedQuestionId) {
      this.loadCommentsForQuestion(this.selectedQuestionId);
    }
  }

  onPageSizeChange(rowsPerView: number): void {
    this.paginationCriteria.rowsPerView = rowsPerView;
    this.paginationCriteria.currentPage = 1;
    if (this.selectedQuestionId) {
      this.loadCommentsForQuestion(this.selectedQuestionId);
    }
  }

  updateDropdownOptions(): void {
    this.dropdownOptions = this.questionComments.map(q => ({
      key: q.question_id.toString(),
      value: this.getQuestionTextWithOrgReplaced(q.question_text)
    }));
  }

  isOptionSelected(): boolean {
    return this.selectedQuestionId !== null && this.selectedQuestionId !== undefined;
  }

  onDropdownChange(event: any): void {
    // The dropdown component emits the option object directly
    // Match the pattern used in filter-v2: event.value contains the display text
    const option = event;
    if (option && option.key) {
      const questionId = parseInt(option.key, 10);
      // Update the label first (matching filter-v2 pattern where they set selectedDate = value)
      this.dropdownLabel = option.value;
      // Then select the question
      this.selectQuestion(questionId);
      // Force change detection
      this.cdr.detectChanges();
    }
  }

  getLikertAnswerText(answer: string): string {
    const answerNum = parseInt(answer, 10);
    const likertMap: { [key: number]: string } = {
      1: $localize`Strongly disagree`,
      2: $localize`Disagree`,
      3: $localize`Neither agree nor disagree`,
      4: $localize`Agree`,
      5: $localize`Strongly agree`
    };
    return likertMap[answerNum] || answer;
  }

  getCommentWithOrgReplaced(comment: string): string {
    if (!comment) return '';
    return comment.replace(/\{\{organisation\}\}/g, this.orgBrandName || 'the organisation');
  }

  getQuestionTextWithOrgReplaced(questionText: string): string {
    if (!questionText) return '';
    return questionText.replace(/\{\{organisation\}\}/g, this.orgBrandName || 'the organisation');
  }

  ngOnDestroy(): void {
    if (this.organisationSubscription) {
      this.organisationSubscription.unsubscribe();
    }
    if (this.questionCommentsSubscription) {
      this.questionCommentsSubscription.unsubscribe();
    }
    if (this.commentsSubscription) {
      this.commentsSubscription.unsubscribe();
    }
  }
}
